/**
 * Job-status helpers for the Syntiro Honey upload flow's polling step.
 */

import { BatchJobRef, Job, SyntiroHoneyJobStatus } from '../../../types/syntiro_honey';

/** Statuses after which a job will never change again — polling can stop. */
export const TERMINAL_JOB_STATUSES: ReadonlyArray<SyntiroHoneyJobStatus> = [
  'DONE',
  'FAILED',
  'CANCELLED'
];

/**
 * Whether a job status is terminal (no further polling needed).
 * @param {SyntiroHoneyJobStatus | undefined} status The last known status, if any.
 * @returns {boolean}
 */
export const isTerminalJobStatus = (status: SyntiroHoneyJobStatus | undefined): boolean => {
  return status !== undefined && TERMINAL_JOB_STATUSES.includes(status);
};

// Keys the backend is likely to use for a failure message inside the loosely
// typed `result` job object.
const ERROR_DETAIL_KEYS: ReadonlyArray<string> = ['error', 'message', 'detail', 'reason'];

/**
 * Best-effort extraction of a human-readable failure detail from a failed
 * job's `result`. Only `result` is scanned — `payload` holds the job's input,
 * so a field there named e.g. `message` would be user data, not a failure
 * reason. `result` is loosely typed on the contract
 * (`Record<string, unknown> | null`), so every value is narrowed before use.
 * @param {Job} job The failed job.
 * @returns {string | null} A failure message, or null when none is present.
 */
export const findJobErrorDetail = (job: Job): string | null => {
  if (job.result === null) {
    return null;
  }
  for (const key of ERROR_DETAIL_KEYS) {
    const candidate: unknown = job.result[key];
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
    if (typeof candidate === 'object' && candidate !== null) {
      const nested_message: unknown = (candidate as Record<string, unknown>).message;
      if (typeof nested_message === 'string' && nested_message.trim() !== '') {
        return nested_message;
      }
    }
  }
  return null;
};

/** Reads a numeric field from the loosely typed job `result`, or null. */
const readCount = (result: Record<string, unknown>, key: string): number | null => {
  const value: unknown = result[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/**
 * The structured extraction outcome of a completed job, in the two-signal
 * language (value vs mapping). Each mapping count is null when the result did
 * not carry it, so the UI can omit the corresponding chip rather than show `0`.
 */
export interface JobResultBreakdown {
  /**
   * Records this upload stored. On an amendment that is ONLY the new/changed
   * ones, while the mapping counts below cover every resolved record of the
   * file — the mismatch reviewers reported, so never render this as the file's
   * total without adding {@link JobResultBreakdown.unchanged}.
   */
  recordCount: number;
  /** Records mapped to a metric with confidence, or null when not reported. */
  mapped: number | null;
  /** Records needing reviewer disambiguation, or null when not reported. */
  needsDisambiguation: number | null;
  /** Records with no candidate metric, or null when not reported. */
  unmapped: number | null;
  /**
   * Datapoints of the amended upload that this one left untouched, so they were
   * carried over instead of re-inserted. Optional rather than nullable: its
   * ABSENCE is what marks the upload as a first upload rather than an amendment,
   * and keeps a first upload's breakdown exactly the shape it always had.
   */
  unchanged?: number;
  /** Records of the amended upload this one replaced, absent on a first upload. */
  superseded?: number;
  /**
   * Records restating a datapoint another file already holds with the SAME value,
   * absent when none — the trigger for the review screen's duplicate-resolution
   * banner (Maxine 2026-08-19 scenario 2).
   */
  duplicates?: number;
  /** Records whose value DISAGREES with another file's, absent when none. */
  conflicts?: number;
}

/**
 * Structured extraction counts from a completed job's `result`, or null when
 * the result carries no record count (e.g. a still-running job, or a format the
 * pipeline skipped — those have a `skipped` marker but no `recordCount`). An
 * amendment's result additionally carries the carried-over and superseded
 * counts; a first upload's does not.
 * @param {Job} job The polled job.
 * @returns {JobResultBreakdown | null} The counts, or null when none are present.
 */
export const getJobResultBreakdown = (job: Job): JobResultBreakdown | null => {
  if (job.result === null) {
    return null;
  }
  // A skipped format reports `recordCount: 0` alongside a `skipped` marker; that
  // is an honest "no extractor yet" message, not a real zero-record extraction,
  // so it has no breakdown (the caller renders the message instead of chips).
  if (typeof job.result.skipped === 'string' && job.result.skipped.trim() !== '') {
    return null;
  }
  const record_count = readCount(job.result, 'recordCount');
  if (record_count === null) {
    return null;
  }
  return {
    recordCount: record_count,
    mapped: readCount(job.result, 'mappedCount'),
    needsDisambiguation: readCount(job.result, 'needsDisambiguationCount'),
    unmapped: readCount(job.result, 'unmappedCount'),
    unchanged: readCount(job.result, 'unchangedCount') ?? undefined,
    superseded: readCount(job.result, 'supersededCount') ?? undefined,
    duplicates: readCount(job.result, 'duplicateCount') ?? undefined,
    conflicts: readCount(job.result, 'conflictCount') ?? undefined
  };
};

/** A live progress reading for the bar: how far along, and what is happening. */
export interface JobProgressReading {
  /** Percentage in [1, 100] — only readings with real progress are returned. */
  percent: number;
  /** Human caption for the current stage, or null when the stage is unknown. */
  caption: string | null;
}

/** Reviewer-facing captions per pipeline stage reported by the backend. */
const STAGE_CAPTIONS: Record<string, string> = {
  PARSING: 'Reading the file…',
  EXTRACTING: 'Extracting data with AI…',
  RESOLVING: 'Matching metrics, entities and periods…',
  SAVING: 'Saving records…'
};

/**
 * The live progress of a RUNNING job, or null when the backend has not reported
 * any real progress yet (progress 0 → the caller keeps its indeterminate bar).
 * When the EXTRACTING stage carries sheet counts, the caption names the exact
 * sheet being read — the honest version of "this can take a while".
 * @param {Job} job The polled job.
 * @returns {JobProgressReading | null} The reading, or null without real progress.
 */
export const getJobProgress = (job: Job): JobProgressReading | null => {
  if (typeof job.progress !== 'number' || job.progress <= 0) {
    return null;
  }
  const percent = Math.max(1, Math.min(100, Math.round(job.progress)));
  const detail = job.progressDetail;
  if (typeof detail !== 'object' || detail === null) {
    return { percent, caption: null };
  }
  const { stage, sheetsTotal, sheetsDone, currentSheet, recordsDone, recordsTotal } =
    detail as Record<string, unknown>;
  if (typeof stage !== 'string') {
    return { percent, caption: null };
  }
  if (
    stage === 'RESOLVING' &&
    typeof recordsDone === 'number' &&
    typeof recordsTotal === 'number' &&
    recordsTotal > 0
  ) {
    return {
      percent,
      caption: `Matching metrics, entities and periods… (${recordsDone.toLocaleString()} of ${recordsTotal.toLocaleString()} records)`
    };
  }
  if (
    stage === 'EXTRACTING' &&
    typeof sheetsDone === 'number' &&
    typeof sheetsTotal === 'number' &&
    sheetsTotal > 0
  ) {
    const sheet_name =
      typeof currentSheet === 'string' && currentSheet.trim() !== '' ? ` — "${currentSheet}"` : '';
    return { percent, caption: `Reading sheet ${sheetsDone} of ${sheetsTotal}${sheet_name}…` };
  }
  return { percent, caption: STAGE_CAPTIONS[stage] ?? null };
};

/** One worksheet the pipeline could not read, with the reason it was set aside. */
export interface SkippedSheetInfo {
  /** The worksheet name. */
  sheet: string;
  /** Why the sheet was skipped (verification evidence / failure message). */
  reason: string;
}

/**
 * The worksheets a completed job had to skip (e.g. a sheet whose structure could
 * not be established), so the uploader learns WHICH parts of their file need a
 * manual look instead of assuming full coverage. `result.skippedSheets` is
 * loosely typed on the contract, so every entry is narrowed before use and
 * malformed entries are dropped.
 * @param {Job} job The polled job.
 * @returns {Array<SkippedSheetInfo>} Skipped sheets, empty when none were reported.
 */
export const getSkippedSheets = (job: Job): Array<SkippedSheetInfo> => {
  if (job.result === null) {
    return [];
  }
  const value: unknown = job.result.skippedSheets;
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const { sheet, reason } = entry as Record<string, unknown>;
    if (typeof sheet !== 'string' || sheet.trim() === '') {
      return [];
    }
    return [
      { sheet, reason: typeof reason === 'string' && reason.trim() !== '' ? reason : 'unknown' }
    ];
  });
};

/**
 * The extracted-records line for a finished file. An amendment stores only the
 * new or changed datapoints while its mapping chips cover every resolved record
 * of the file, so the line names both populations — otherwise "2 records
 * extracted" beside "37 mapped" reads as a bug (Maxine, 2026-08-03).
 *
 * Whether this IS an amendment is read from presence, not value: `unchanged`
 * and `superseded` are `undefined` on a first upload and defined (even at 0) on
 * an amendment (see {@link JobResultBreakdown.unchanged}) — collapsing that via
 * `?? 0` would mislabel a genuine "changed nothing" amendment as a first upload.
 * @param {JobResultBreakdown} breakdown The completed job's extraction counts.
 * @returns {string} The line to render above the mapping chips.
 */
export const describeExtractedRecords = (breakdown: JobResultBreakdown): string => {
  const is_first_upload = breakdown.unchanged === undefined && breakdown.superseded === undefined;
  if (is_first_upload) {
    return `${breakdown.recordCount} record${breakdown.recordCount === 1 ? '' : 's'} extracted`;
  }
  const carried_over = breakdown.unchanged ?? 0;
  const total = breakdown.recordCount + carried_over;
  const populations: Array<string> = [`${breakdown.recordCount} new or changed`];
  if (carried_over > 0) {
    populations.push(`${carried_over} unchanged (carried over)`);
  }
  return `${total} record${total === 1 ? '' : 's'} extracted — ${populations.join(', ')}`;
};

/**
 * Summarise a completed ingestion job's extraction outcome as one line, e.g.
 * `10 records extracted — 7 mapped, 2 to disambiguate, 1 unmapped`. The
 * pipeline skips formats without an extractor yet (`skipped` set on the
 * result); that case is reported honestly instead of as "0 records". Reuses
 * {@link describeExtractedRecords} for the count clause so this line and the
 * one JobProgressList renders directly can never disagree on it again.
 * @param {Job} job The polled job.
 * @returns {string | null} The summary line, or null when the result carries no counts.
 */
export const summariseJobResult = (job: Job): string | null => {
  if (job.result === null) {
    return null;
  }
  if (typeof job.result.skipped === 'string' && job.result.skipped.trim() !== '') {
    return 'No extractor for this format yet — 0 records (coming in a later phase).';
  }
  const breakdown = getJobResultBreakdown(job);
  if (breakdown === null) {
    return null;
  }
  const parts: Array<string> = [];
  if (breakdown.mapped !== null) parts.push(`${breakdown.mapped} mapped`);
  if (breakdown.needsDisambiguation !== null)
    parts.push(`${breakdown.needsDisambiguation} to disambiguate`);
  if (breakdown.unmapped !== null) parts.push(`${breakdown.unmapped} unmapped`);
  const base = describeExtractedRecords(breakdown);
  return parts.length > 0 ? `${base}; ${parts.join(', ')}` : base;
};

/**
 * Format a non-negative millisecond duration as `m:ss` (or `h:mm:ss` past an
 * hour). A negative, non-finite, or otherwise nonsensical input clamps to
 * `0:00`, so a clock skew or an unparseable timestamp never renders a garbage
 * timer.
 * @param {number} millis The duration in milliseconds.
 * @returns {string} The formatted duration.
 */
export const formatDuration = (millis: number): string => {
  const total_seconds = Number.isFinite(millis) && millis > 0 ? Math.floor(millis / 1000) : 0;
  const seconds = total_seconds % 60;
  const minutes = Math.floor(total_seconds / 60) % 60;
  const hours = Math.floor(total_seconds / 3600);
  const ss = String(seconds).padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`;
  }
  return `${minutes}:${ss}`;
};

/**
 * Elapsed wall-clock time for a job in milliseconds: from its `createdAt` up to
 * `nowMs` while it is still running, frozen at `updatedAt` once it is terminal.
 * An unparseable timestamp yields 0 so the caller renders `0:00` rather than a
 * crash — the same Invalid-Date class of failure the extraction pipeline hit.
 * @param {Job} job The job to measure.
 * @param {number} nowMs The current epoch time in milliseconds (for a live job).
 * @returns {number} The elapsed time in milliseconds, never negative.
 */
export const jobElapsedMillis = (job: Job, nowMs: number): number => {
  const start_ms = Date.parse(job.createdAt);
  if (Number.isNaN(start_ms)) {
    return 0;
  }
  const end_ms = isTerminalJobStatus(job.status) ? Date.parse(job.updatedAt) : nowMs;
  if (Number.isNaN(end_ms)) {
    return 0;
  }
  return Math.max(0, end_ms - start_ms);
};

/** Aggregate progress across all of a batch's jobs, for the batch-level header. */
export interface BatchProgressSummary {
  /** Total jobs in the batch. */
  total: number;
  /** Jobs still waiting for a worker (PENDING). */
  queued: number;
  /** Jobs currently running (RUNNING). */
  processing: number;
  /** Jobs that finished successfully (DONE). */
  done: number;
  /** Jobs that failed (FAILED). */
  failed: number;
  /** Jobs that were cancelled (CANCELLED). */
  cancelled: number;
  /** Jobs in any terminal state (done + failed + cancelled). */
  finished: number;
  /** Percentage of jobs finished, rounded, in [0, 100]. */
  percentComplete: number;
}

/**
 * Tally a batch's per-file job statuses into one aggregate for the header
 * (overall progress + a per-state count). A job not yet polled counts as queued
 * (PENDING), matching the per-file row's default.
 * @param {ReadonlyArray<BatchJobRef>} jobRefs One reference per queued file.
 * @param {Record<string, Job>} jobsById Last polled state per job id.
 * @returns {BatchProgressSummary} The aggregate counts and overall percentage.
 */
export const summariseBatchProgress = (
  jobRefs: ReadonlyArray<BatchJobRef>,
  jobsById: Record<string, Job>
): BatchProgressSummary => {
  const counts = { queued: 0, processing: 0, done: 0, failed: 0, cancelled: 0 };
  for (const ref of jobRefs) {
    const status: SyntiroHoneyJobStatus = jobsById[ref.jobId]?.status ?? 'PENDING';
    switch (status) {
      case 'RUNNING':
        counts.processing += 1;
        break;
      case 'DONE':
        counts.done += 1;
        break;
      case 'FAILED':
        counts.failed += 1;
        break;
      case 'CANCELLED':
        counts.cancelled += 1;
        break;
      default:
        counts.queued += 1;
        break;
    }
  }
  const total = jobRefs.length;
  const finished = counts.done + counts.failed + counts.cancelled;
  const percentComplete = total === 0 ? 0 : Math.round((finished / total) * 100);
  return { total, ...counts, finished, percentComplete };
};
